import type { FoodItem, MenuResponse } from './types';
import photoPolicy from '@/data/dining-photo-catalog.v1.json';

export const HALL_IMAGE_MAP = {
  livingston: '/dining/halls/livingston.png',
  busch: '/dining/halls/busch.png',
  neilson: '/dining/halls/neilson.png',
} as const;

export const DINING_IMAGE_SOURCES = {
  halls: {
    livingston: 'https://food.rutgers.edu/places-eat/dining-hall-tour',
    busch: 'https://food.rutgers.edu/places-eat/dining-hall-tour',
    neilson: 'https://food.rutgers.edu/places-eat/dining-hall-tour',
  },
  food: {
    pizza: 'https://food.rutgers.edu/places-eat/dining-hall-tour',
    burger: 'https://www.pexels.com/photo/close-up-of-a-burger-12932961/',
    pasta: 'https://www.pexels.com/photo/bowl-of-pasta-18712233/',
    'grain-bowl': 'https://www.pexels.com/photo/healthy-grain-bowl-with-vegetables-19870650/',
    noodles: 'https://www.pexels.com/photo/asian-noodle-bowl-on-a-table-19264278/',
    tacos: 'https://www.pexels.com/photo/delicious-tacos-on-a-table-27603257/',
    soup: 'https://www.pexels.com/photo/soup-in-a-bowl-16355469/',
    sandwich: 'https://www.pexels.com/photo/close-up-photo-of-sandwich-12318097/',
    chicken: 'https://www.pexels.com/photo/a-delicious-grilled-chicken-on-a-sizzling-plate-5893501/',
    seafood: 'https://www.pexels.com/photo/seafood-on-plate-19725431/',
    'stir-fry': 'https://food.rutgers.edu/places-eat/dining-hall-tour',
    salad: 'https://food.rutgers.edu/places-eat/dining-hall-tour',
    breakfast: 'https://www.pexels.com/photo/breakfast-on-a-plate-15043913/',
    dessert: 'https://food.rutgers.edu/places-eat/dining-hall-tour',
    fruit:
      'https://commons.wikimedia.org/wiki/File:Colorful_assortment_of_fresh_fruits_arranged_in_a_bowl_with_natural_light_highlighting_their_vibrant_colors.jpg',
    vegetables:
      'https://commons.wikimedia.org/wiki/File:Sunday_roast_vegetable_side_dish_at_The_Stag,_Little_Easton,_Essex,_England.jpg',
    bread: 'https://commons.wikimedia.org/wiki/File:Basket_of_rolls.jpg',
    cheese: 'https://commons.wikimedia.org/wiki/File:Kaasschotel_(1).jpg',
    condiments: 'https://commons.wikimedia.org/wiki/File:Ranch_dressing.jpg',
    potatoes: 'https://commons.wikimedia.org/wiki/File:Truffle_oil_french_fries_(33024792848).jpg',
    yogurt:
      'https://commons.wikimedia.org/wiki/File:Yogurt_parfait_with_granola_and_blueberries_in_shot_glasses_with_silverware_spoons_(17098581522).jpg',
    curry: 'https://commons.wikimedia.org/wiki/File:Chicken_tikka_masala.jpg',
    rice: 'https://commons.wikimedia.org/wiki/File:Steamed_rice_in_bowl_01.jpg',
    sushi: 'https://commons.wikimedia.org/wiki/File:Sushi_rolls_placed_on_their_display_stand.jpg',
    default: 'generated in-repo (generic buffet spread)',
  },
} as const;

// Ordered list: the first rule whose keyword appears in the item name/description
// wins, so more specific dishes must come before generic ingredient buckets
// (e.g. "chicken pot pie" should hit the meat rule before "pie" hits dessert).
const FOOD_IMAGE_RULES: Array<{ src: string; keywords: string[] }> = [
  {
    src: '/dining/food/pizza.jpg',
    keywords: ['pizza', 'margherita', 'flatbread', 'calzone', 'pepperoni'],
  },
  {
    src: '/dining/food/tacos.jpg',
    keywords: [
      'taco', 'burrito', 'quesadilla', 'enchilada', 'fajita', 'nacho', 'mexican',
      'tortilla', 'refried', 'pico de gallo', 'guacamole', 'avocado mash', 'salsa',
    ],
  },
  {
    src: '/dining/food/burger-new.jpg',
    keywords: ['burger', 'cheeseburger', 'hamburger', 'slider'],
  },
  {
    src: '/dining/food/pasta.jpg',
    keywords: [
      'pasta', 'spaghetti', 'penne', 'linguine', 'ravioli', 'lasagna', 'macaroni',
      'mac & cheese', 'mac and cheese', 'ziti', 'tortellini', 'rigatoni', 'rotini',
      'orzo', 'alfredo', 'marinara', 'vodka sauce', 'gnocchi',
    ],
  },
  {
    src: '/dining/food/sushi.jpg',
    keywords: ['sushi', 'california roll', 'wakame', 'seaweed', 'pickled ginger', 'wasabi', 'red roe', 'nori'],
  },
  {
    src: '/dining/food/noodles.jpg',
    keywords: ['lo mein', 'ramen', 'udon', 'noodle', 'pad thai', 'chow mein', 'asian'],
  },
  {
    src: '/dining/food/stir-fry.jpg',
    keywords: [
      'stir fry', 'mongolian', 'teriyaki', 'fried rice', 'potsticker', 'dumpling',
      'dim sum', 'general tso', 'szechuan', 'lemongrass', 'hibachi', 'egg roll', 'spring roll',
    ],
  },
  {
    src: '/dining/food/curry.jpg',
    keywords: [
      'curry', 'tikka', 'masala', 'paneer', 'tadka', 'samosa', 'pakora', 'chana',
      'biryani', 'korma', 'tandoori', 'aloo gobi', 'vindaloo', 'saag', 'palak',
    ],
  },
  {
    src: '/dining/food/grain-bowl.jpg',
    keywords: ['grain bowl', 'rice bowl', 'quinoa', 'couscous', 'lentil', 'falafel', 'chickpea', 'tofu', 'tempeh'],
  },
  {
    src: '/dining/food/soup.jpg',
    keywords: ['soup', 'chili', 'chowder', 'bisque', 'stew', 'broth', 'gumbo'],
  },
  {
    src: '/dining/food/breakfast-new.jpg',
    keywords: [
      'breakfast', 'omelet', 'omelette', 'pancake', 'waffle', 'egg', 'bacon',
      'sausage', 'bagel', 'hash brown', 'french toast',
    ],
  },
  {
    src: '/dining/food/sandwich.jpg',
    keywords: ['sandwich', 'panini', 'wrap', 'sub ', 'hoagie', 'club', 'grilled cheese'],
  },
  {
    src: '/dining/food/seafood.jpg',
    keywords: [
      'fish', 'salmon', 'tilapia', 'shrimp', 'seafood', 'tuna', 'cod', 'flounder',
      'crab', 'lobster', 'calamari', 'scallop', 'mussel', 'clam', 'mahi', 'lox', 'baked sole',
    ],
  },
  {
    src: '/dining/food/chicken.jpg',
    keywords: [
      'chicken', 'turkey', 'beef', 'steak', 'pork', 'meatball', 'roast', ' ham',
      'lamb', 'wings', 'ribs', 'brisket', 'meatloaf', 'london broil', 'pastrami',
      'salami', 'kebab', 'gyro', 'rotisserie', 'pot pie',
    ],
  },
  {
    src: '/dining/food/dessert.jpg',
    keywords: [
      'dessert', 'cookie', 'cake', 'brownie', 'ice cream', 'pie', 'pudding',
      'muffin', 'cupcake', 'donut', 'doughnut', 'pastry', 'danish', 'churro',
      'cobbler', 'graham', 'gelato', 'sorbet',
    ],
  },
  {
    src: '/dining/food/yogurt.jpg',
    keywords: ['yogurt', 'granola', 'parfait', 'oatmeal', 'grits'],
  },
  {
    src: '/dining/food/cheese.jpg',
    keywords: ['cheese', 'feta', 'mozzarella', 'cheddar', 'provolone', 'queso'],
  },
  {
    src: '/dining/food/bread.jpg',
    keywords: [
      'bread', 'roll', 'pita', 'naan', 'croissant', 'biscuit', 'baguette',
      'focaccia', 'bun', 'crostini', 'ciabatta', 'brioche',
    ],
  },
  {
    src: '/dining/food/potatoes.jpg',
    keywords: ['fries', 'potato', 'tater tot', 'tots'],
  },
  {
    src: '/dining/food/salad.jpg',
    keywords: ['salad', 'romaine', 'spinach', 'greens', 'caesar', 'cucumber', 'tomato', 'lettuce', 'coleslaw', 'slaw'],
  },
  {
    src: '/dining/food/vegetables.jpg',
    keywords: [
      'vegetable', 'veggie', 'broccoli', 'carrot', 'cauliflower', 'beet', 'onion',
      'mushroom', 'pepper', 'corn', 'bean', 'olive', 'artichoke', 'cabbage',
      'zucchini', 'squash', 'asparagus', 'peas', 'edamame', 'celery', 'jalapeno',
      'cilantro', 'scallion', 'sprout', 'bamboo', 'avocado', 'pickle', 'kale',
      'brussels', 'okra', 'eggplant', 'bok choy', 'portabella', 'portobello', 'collard',
    ],
  },
  {
    src: '/dining/food/fruit.jpg',
    keywords: [
      'fruit', 'banana', 'apple', 'orange', 'grape', 'melon', 'cantaloupe',
      'canteloupe', 'honeydew', 'pineapple', 'strawberr', 'blueberr', 'kiwi',
      'peach', 'pear', 'mandarin', 'raisin', 'craisin', 'plum', 'lime', 'lemon',
    ],
  },
  {
    src: '/dining/food/condiments.jpg',
    keywords: [
      'dressing', 'vinaigrette', 'vinagrette', 'sauce', 'vinegar', ' oil', 'ketchup',
      'mustard', 'mayo', 'hummus', 'dip', 'syrup', 'honey', 'butter', 'sour cream',
      'crouton', 'seeds', 'relish', 'gravy', 'tahini', 'coconut milk', 'jelly',
    ],
  },
  {
    src: '/dining/food/rice.jpg',
    keywords: ['rice', 'farro', 'barley', 'sorghum', 'wheat berries', 'pilaf', 'risotto'],
  },
];

export function getFoodImageSrc(item: FoodItem) {
  const name = item.name.toLowerCase();
  if (/\b(bun|roll)\b/.test(name) && /\b(burger|hamburger|hot dog)\b/.test(name)) return '/dining/food/bread.jpg';
  if (/\bhot dogs?\b|\bcorn dogs?\b/.test(name) || /\b(black bean|vegan|veggie|vegetarian|turkey|chicken|fish)\b.*\bburger\b/.test(name)) return '/dining/food/default.jpg';
  const searchableText = `${item.name} ${item.description}`.toLowerCase();
  const matchedRule = FOOD_IMAGE_RULES.find((rule) =>
    rule.keywords.some((keyword) => searchableText.includes(keyword))
  );

  return matchedRule?.src ?? '/dining/food/default.jpg';
}

export type DiningItemImageRecipe = {
  src: string | null;
  variantId: string;
  kind: 'provider' | 'illustrative' | 'none';
  category?: string;
  sourceURL?: string;
};

export function diningProviderImageURL(item: FoodItem): string | null {
  try {
    const url = new URL(item.image_url?.trim() ?? '');
    return url.protocol === 'https:' && url.hostname && !url.username && !url.password ? url.href : null;
  } catch {
    return null;
  }
}

function normalizedName(name: string) { return name.toLowerCase().trim().replace(/\s+/g, ' '); }
function containsTerm(name: string, term: string) { return new RegExp(`\\b${term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`).test(name); }
function compatibleIllustration(item: FoodItem, rule: typeof photoPolicy.rules[number]) {
  const plantBased = item.dietary?.vegetarian || item.dietary?.vegan;
  return !(plantBased && rule.containsMeat) && !(item.dietary?.vegan && rule.containsDairy);
}

/** Both clients honor the same catalog/provenance contract. Never use an
 * example to infer ingredients or appearance, or reuse it for a different dish. */
export function buildDiningItemImageRegistry(menu: MenuResponse | null) {
  const items = menu?.stations.flatMap((station) => station.items).sort((left, right) => {
    const leftName = normalizedName(left.name), rightName = normalizedName(right.name);
    return leftName === rightName ? left.id - right.id : leftName < rightName ? -1 : 1;
  }) ?? [];
  const usedImages = new Set<string>();
  const photoByName = new Map<string, DiningItemImageRecipe>();
  const result = new Map<number, DiningItemImageRecipe>();
  for (const item of items) {
    const variantId = normalizedName(item.name);
    const providerURL = diningProviderImageURL(item);
    if (providerURL) {
      result.set(item.id, { src: providerURL, variantId, kind: 'provider', sourceURL: providerURL });
      continue;
    }
    const assigned = photoByName.get(variantId);
    if (assigned) {
      const rule = photoPolicy.rules.find((rule) => rule.webPath === assigned.src)!;
      if (compatibleIllustration(item, rule)) result.set(item.id, assigned);
      continue;
    }
    if (photoPolicy.excludedNameWords.some((term) => containsTerm(variantId, term))) continue;
    const rule = photoPolicy.rules.find((rule) => rule.terms.some((term) => containsTerm(variantId, term)));
    if (!rule || usedImages.has(rule.webPath) || !compatibleIllustration(item, rule) || (variantId.includes('salad') && rule.category !== 'salad')) continue;
    const recipe: DiningItemImageRecipe = { src: rule.webPath, variantId, kind: 'illustrative', category: rule.category, sourceURL: rule.sourceURL };
    usedImages.add(rule.webPath);
    photoByName.set(variantId, recipe);
    result.set(item.id, recipe);
  }
  return result;
}
